import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useMemo } from "react";
import Session from "supertokens-web-js/recipe/session";

import type { AccessTokenPayload } from "@/helpers/jwt";
import { parseAccessTokenPayload } from "@/helpers/jwt";
import { sessionIdentityQueryOptions } from "@/queries/authentication/queryOptions";

interface AccessTokenContext {
  connectionScope: object;
  payload?: AccessTokenPayload;
  isPending: boolean;
  error?: unknown;
  getAccessToken: () => Promise<string | undefined>;
  getAccessTokenPayload: () => Promise<AccessTokenPayload | undefined>;
}

const AccessTokenContext = createContext<AccessTokenContext | null>(null);

export function useAccessTokenContext(): AccessTokenContext {
  const context = useContext(AccessTokenContext);
  if (!context) {
    throw new Error("Credential provider is missing");
  }

  return context;
}

/** Supplies live SDK token access plus one cached identity read shared by all dashboard consumers. */
export function SuperTokensAccessTokenProvider({ children }: { children: React.ReactNode }): React.ReactNode {
  const identity = useQuery(sessionIdentityQueryOptions());
  const sessionIdentity = identity.data?.sessionHandle ?? identity.data?.sub;
  const connectionScope = useMemo(() => ({ sessionIdentity }), [sessionIdentity]);
  const accessTokenContextValue = useMemo(
    () => ({
      getAccessToken: async () => {
        if (await Session.doesSessionExist()) {
          return Session.getAccessToken();
        }
      },
      getAccessTokenPayload: async () => {
        if (await Session.doesSessionExist()) {
          const accessToken = await Session.getAccessToken();

          if (accessToken) {
            return parseAccessTokenPayload(accessToken);
          }
        }
      },
    }),
    [],
  );

  return (
    <AccessTokenContext
      value={{
        ...accessTokenContextValue,
        connectionScope,
        payload: identity.data ?? undefined,
        isPending: identity.isPending || (identity.isFetching && !identity.data),
        error: identity.error,
      }}
    >
      {children}
    </AccessTokenContext>
  );
}

/** Supplies quick-reply credentials synchronously. Token identity changes create a separate SignalR scope. */
export function RawAccessTokenProvider({
  token,
  children,
}: {
  token: string;
  children: React.ReactNode;
}): React.ReactNode {
  const accessTokenValueContext = useMemo(
    () => ({
      connectionScope: {},
      payload: parseAccessTokenPayload(token),
      isPending: false,
      getAccessToken: () => Promise.resolve(token),
      getAccessTokenPayload: () => Promise.resolve(parseAccessTokenPayload(token)),
    }),
    [token],
  );

  return <AccessTokenContext value={accessTokenValueContext}>{children}</AccessTokenContext>;
}
